// Central source of truth for Voices of Change podcast episodes.
// Rich metadata drives both the /podcast listing and per-episode SEO show-notes pages.
// Episodes without full metadata are marked "status: 'archive'" and link straight to YouTube.
export const EPISODES = [
  {
    slug: "ep-01-measuring-performance-olympic-legacy-building-trust",
    number: "S1 · E01",
    ep_num: 1,
    title: "Measuring performance, Olympic Legacy, Building Trust - It All Matters",
    guest: "Dr Marcus Curcija",
    guest_bio: "Dr Marcus Curcija is Founder of Insight into Impact. He specialises in translating community perspectives, vulnerability and lived experience into practical impact measurement for business, government and NGOs.",
    guest_links: [
      { label: "insightintoimpact.com.au", url: "https://insightintoimpact.com.au" },
      { label: "LinkedIn", url: "https://www.linkedin.com/in/marcuscurcija/" },
    ],
    duration: "1h 5m",
    duration_iso: "PT65M21S",
    releaseDate: "7 April 2025",
    iso_date: "2025-04-07",
    excerpt: "Dr Marcus Curcija unpacks social value, social performance and social impact - and what genuine Brisbane 2032 legacy could look like when community voices lead.",
    long_summary: "In this launch conversation, Andrea Kanaris and Dr Marcus Curcija cut through the fog around social value, social impact and social performance - and why the distinctions matter for business, government and the communities they touch. They dig into vulnerability beyond the usual definitions, the role of business as a co-creator of social value, and how the Brisbane 2032 Olympics could either leave a genuine community legacy or a set of stranded assets. The episode closes on data, listening and the practical craft of turning community voice into measurable performance.",
    status: "published",
    youtube_id: "mBqZAKE94Rc",
    youtube_url: "https://youtu.be/mBqZAKE94Rc",
    chapters: [
      { t: "0:00", label: "Introduction to Social Impact and Personal Background" },
      { t: "5:11", label: "Defining Key Terms: Social Value, Impact, and Performance" },
      { t: "12:04", label: "The Role of Business in Social Impact" },
      { t: "17:47", label: "Understanding Vulnerability in Communities" },
      { t: "23:54", label: "The Importance of Perspective in Social Impact Assessments" },
      { t: "29:39", label: "Technical Skills for Social Impact Research" },
      { t: "34:19", label: "The Importance of Social Impact Assessments" },
      { t: "38:14", label: "Legacy and the Olympics: A Case Study" },
      { t: "43:17", label: "Community Engagement and Future Planning" },
      { t: "47:59", label: "Listening to Communities: The Key to Success" },
      { t: "54:07", label: "Data-Driven Approaches to Social Impact" },
      { t: "1:02:43", label: "Innovative Solutions for Social Performance" },
      { t: "1:05:11", label: "Closer" },
    ],
    takeaways: [
      "Social value is about making people's lives materially better - not just measurable.",
      "Business is a co-creator of social value; stakeholder input has to count.",
      "Vulnerability extends well beyond the demographic categories most projects use.",
      "Olympic legacies only survive if community voice leads planning.",
      "Data informs strategy, but listening is what makes strategy land.",
    ],
    keywords: ["social impact", "impact measurement", "impact management", "community engagement", "Brisbane 2032", "Olympic legacy", "vulnerability", "social value", "social performance"],
  },
  {
    slug: "ep-02-real-impact-sustainable-development-paul-cranch",
    number: "S1 · E02",
    ep_num: 2,
    title: "What Real Impact Looks Like in Sustainable Development",
    guest: "Paul Cranch",
    guest_bio: "Paul Cranch is a senior sustainable-development practitioner who has spent decades working alongside rural communities, local government and community-led resilience programs across regional Australia and internationally.",
    guest_links: [
      { label: "LinkedIn (search Paul Cranch)", url: "https://www.linkedin.com/search/results/people/?keywords=paul%20cranch" },
    ],
    duration: "59 min",
    duration_iso: "PT59M38S",
    releaseDate: "21 April 2025",
    iso_date: "2025-04-21",
    excerpt: "Rural communities, local government and community-led resilience - Paul Cranch on how relationships, education and honest engagement create lasting economic and social outcomes.",
    long_summary: "Andrea Kanaris and Paul Cranch trade notes on what sustainable development actually delivers on the ground - versus what looks good in a report. They cover the primacy of relationships in rural contexts, why education and capacity-building outlast infrastructure, how local government levers get under-used, and where community-led resilience is quietly outperforming top-down programs.",
    status: "published",
    youtube_id: "G3_K7gWXSjk",
    youtube_url: "https://youtu.be/G3_K7gWXSjk",
    chapters: [],
    takeaways: [
      "Sustainable development is a relationship discipline before it is a technical one.",
      "Education and capability-building outlast most infrastructure programs.",
      "Local government has more leverage than most communities give it credit for.",
      "Community-led resilience is the pattern that keeps working when funding stops.",
    ],
    keywords: ["sustainable development", "rural communities", "local government", "community resilience", "education", "capacity building"],
  },
  {
    slug: "ep-03-lessons-from-the-field-richard-parsons",
    number: "S1 · E03",
    ep_num: 3,
    title: "Lessons from the Field - Social Impact, Ethics & Collaboration",
    guest: "Richard Parsons",
    guest_bio: "Richard Parsons is a social scientist with roots in Queensland horse-racing policy who moved through government roles into private-sector consulting. He contributed to the NSW Social Impact Assessment guideline and champions professional ethics and suitably-qualified practice.",
    guest_links: [
      { label: "LinkedIn (search Richard Parsons)", url: "https://www.linkedin.com/search/results/people/?keywords=richard%20parsons%20social%20impact" },
    ],
    duration: "55 min",
    duration_iso: "PT55M39S",
    releaseDate: "5 May 2025",
    iso_date: "2025-05-05",
    excerpt: "From the horse-racing betting floor to shaping the NSW SIA guideline - Richard Parsons on public interest, professional integrity and why suitably-qualified practitioners matter.",
    long_summary: "Andrea Kanaris and Richard Parsons trace his path through social sciences, government roles and private consulting - and land on the ethical spine of the profession. They cover community engagement done properly, the friction of working in social impact, professional integrity when clients push back, and why networks and mentors decide who lasts in the field.",
    status: "published",
    youtube_id: "ZiON-8jTklA",
    youtube_url: "https://youtu.be/ZiON-8jTklA",
    chapters: [],
    takeaways: [
      "Public interest is the north star; every SIA decision has to trace back to it.",
      "Professional integrity is tested when the client wants a softer finding.",
      "Suitably-qualified practice is not gatekeeping - it is community protection.",
      "Networks and mentors decide who lasts more than any technical skill.",
    ],
    keywords: ["social impact assessment", "ethics", "NSW SIA guideline", "public interest", "professional integrity", "collaboration"],
  },
  {
    slug: "ep-04-ai-compliance-community-ildiko-simsic",
    number: "S1 · E04",
    ep_num: 4,
    title: "AI, Compliance & Community - The Future of Social Impact",
    guest: "Ildiko Almasi Simsic",
    guest_bio: "Ildiko Almasi Simsic is a social development specialist, author and founder with 15+ years across resettlement, labour rights, supply chains and Environmental & Social (ENS) practice globally. She built MyESRA - an AI-assisted tool for ENS risk assessment.",
    guest_links: [
      { label: "MyESRA - AI-assisted ENS tool", url: "https://myesra.com" },
      { label: "LinkedIn (search Ildiko Almasi Simsic)", url: "https://www.linkedin.com/search/results/people/?keywords=ildiko%20almasi%20simsic" },
    ],
    duration: "59 min",
    duration_iso: "PT59M55S",
    releaseDate: "19 May 2025",
    iso_date: "2025-05-19",
    excerpt: "From resettlement and labour rights to supply chains and AI tools like MyESRA - lessons from 15+ years of global ENS practice, and why emotional intelligence still trumps technology on site.",
    long_summary: "Andrea Kanaris and Ildiko Simsic look at where AI genuinely raises the bar in environmental and social risk work - and where it quietly makes practice worse. They cover MyESRA and what a good AI tool actually does for a practitioner, compliance versus lived-experience impact, why emotional intelligence still wins on site, and how supply-chain due diligence is reshaping ENS work.",
    status: "published",
    youtube_id: "M01ePwyKwYY",
    youtube_url: "https://youtu.be/M01ePwyKwYY",
    chapters: [],
    takeaways: [
      "AI raises the floor on ENS practice but does not raise the ceiling.",
      "Compliance and impact are not the same target - do not conflate them.",
      "Emotional intelligence beats technology on any real site visit.",
      "Supply-chain due diligence is quietly the biggest growth area in ENS.",
    ],
    keywords: ["AI in social impact", "MyESRA", "ENS", "environmental and social risk", "resettlement", "labour rights", "supply chains", "compliance"],
  },
];

export const ALL_EPISODES_COUNT = 36; // Confirmed by host - full library on YouTube channel
export const YOUTUBE_CHANNEL_URL = "https://www.youtube.com/@AllThingsSocialImpact";
export const SPOTIFY_SHOW_ID = "4RLkmfuAUYC8XVoeISCzKv";

export function findEpisode(slug) {
  return EPISODES.find((e) => e.slug === slug);
}
